import {byProp, byPropDesc, numericCompareAsc} from '@lib/utils';
import {createSelector} from '@reduxjs/toolkit';
import pickBy from 'lodash/pickBy';
import createCachedSelector from 're-reselect';
import {RootState} from '../../rootReducer';
import {Desk, Desks} from '../entities';
import {desksAdapter, suggestedDesksAdapter} from './adapters';

export const {
  selectById: getDeskById,
  selectIds: getDeskIds,
  selectEntities: getDeskEntities,
  selectAll: getAllDesks,
  selectTotal: getTotalDesks,
} = desksAdapter.getSelectors((state: RootState) => state.data.desks.list);

export const {
  selectIds: getSuggestedDeskIds,
  selectEntities: getSuggestedDeskEntities,
  selectAll: getAllSuggestedDesks,
  selectTotal: getTotalSuggestedDesks,
} = suggestedDesksAdapter.getSelectors((state: RootState) => state.data.desks.suggestedDesks);

export const getDesksByAreaId = createCachedSelector(
  getDeskEntities,
  (_: RootState, areaId: string) => areaId,
  (desks, areaId) => {
    const items = pickBy<Desk>(desks as Desks, (desk) => desk.areaId === areaId);
    return Object.values(items).sort(byProp('name'));
  },
)((_: RootState, areaId: string) => areaId);

export const getDeskBookingStatusEntities = (state: RootState) => state.data.desks.deskBookingStatus.entities;

export const getIsDeskBookedByDeskId = createCachedSelector(
  getDeskBookingStatusEntities,
  (_: RootState, deskId: string) => deskId,
  (bookingStatus, deskId) => {
    return bookingStatus[deskId];
  },
)((_, deskId) => deskId);

export const getSuggestedDesks = createSelector(
  getDeskEntities,
  getAllSuggestedDesks,
  (_: RootState, deskCount?: number) => deskCount ?? 1,
  (desks, suggestedDesks, deskCount) => {
    const sortedMatchingDesks = suggestedDesks
      // Note: this does not work for suggestions that were retrieved passing a currentReservationId.
      //       those suggestions will have booked: true, while they are in fact booked by you and you
      //       do have the option of changing them! This will be fixed in the back-end at some point.
      .filter((suggestedDesk) => !suggestedDesk.booked && desks[suggestedDesk.id]?.isEnabled)
      .sort(byPropDesc('score', numericCompareAsc));
    if (sortedMatchingDesks.length >= deskCount) {
      const bestMatchingDesks = sortedMatchingDesks.slice(0, deskCount);
      return bestMatchingDesks;
    }
  },
);

export const getIsLoadingDesks = (state: RootState) => state.data.desks.list.isLoading;

export const getIsLoadingSuggestedDesks = (state: RootState) => state.data.desks.suggestedDesks.isLoading;

export const getIsLoadingDeskBookingStatus = (state: RootState) => state.data.desks.deskBookingStatus.isLoading;

export const getIsLoadingDeskSuggestions = (state: RootState) => state.data.desks.isLoadingDeskSuggestions;

export const getIsLoadingDesksByAreaId = (state: RootState) => state.data.desks.isLoadingDeskByAreaId;

export const getSuggestedDeskNodeIds = (state: RootState) => state.data.desks.suggestedDeskIds;

export const getSuggestionError = (state: RootState) => state.data.desks.suggestionError;
